import React, { useEffect } from 'react';
import { uniq } from 'lodash';
import {
  Form,
  FormInstance,
  DateSerialized,
  CheckboxGroup
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import ScheduleRepeat from '../repeat';
import { CardBlock, CardBreakout } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import {
  EpxVitalsModuleKey,
  IEpxVitalsSchedule
} from '../../../../libs/apps/epxvitals';
import { useScheduleForm } from '../../../../contexts/form';

interface EpxVitalsModuleData {
  isActive: boolean;
}

interface EpxVitalsData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  mode: string;
  step: number;
  modules: Partial<Record<EpxVitalsModuleKey, EpxVitalsModuleData>>;
}

type EpxVitalsDataRaw = Omit<
  DateSerialized<EpxVitalsData, 'start' | 'end'>,
  'modules'
> & {
  modules: string[];
};

export const EpxVitalsDefaultData: EpxVitalsData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  mode: 'W',
  days: [3],
  step: 1,
  modules: {
    temperature: {
      isActive: true
    },
    systolicBP: {
      isActive: true
    },
    diastolicBP: {
      isActive: true
    },
    heartRate: {
      isActive: true
    },
    oxygen: {
      isActive: true
    },
    weight: {
      isActive: true
    },
    requestCall: {
      isActive: true
    }
  }
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxVitalsFormProps {
  setForm?: (form: FormInstance<EpxVitalsDataRaw>) => void;
  onSubmit?: (data: EpxVitalsData) => void;
  rawSchedule?: IEpxVitalsSchedule;
  isDisabled?: boolean;
}

const EpxVitalsForm = ({
  setForm,
  onSubmit: _onSubmit,
  rawSchedule,
  isDisabled
}: EpxVitalsFormProps): JSX.Element => {
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxVitalsDataRaw): EpxVitalsData => {
    const modules: EpxVitalsData['modules'] = {};
    const defaultModules = EpxVitalsDefaultData.modules;
    /**
     * Explicitly setting each module isActive
     * to true or false is necessary for the backend
     * to properly set each module's active status
     */
    for (const key in defaultModules) {
      let isActive = false;
      if (key === 'systolicBP' || key === 'diastolicBP') {
        if (data.modules.includes('bloodPressure')) {
          isActive = true;
        }
      } else {
        if (data.modules.includes(key)) {
          isActive = true;
        }
      }
      modules[key] = {
        isActive
      };
    }
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      modules: modules
    };
  };
  const deserialize = (data: EpxVitalsData): EpxVitalsDataRaw => {
    let modules: string[] = [];
    for (const key of Object.keys(data.modules)) {
      if (data.modules[key].isActive) {
        if (key === 'systolicBP' || key === 'diastolicBP') {
          modules.push('bloodPressure');
        } else {
          modules.push(key);
        }
      }
    }
    modules = uniq(modules);
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      modules
    };
  };
  const defaultData = deserialize(EpxVitalsDefaultData);
  const form = new FormInstance<EpxVitalsDataRaw, EpxVitalsData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxVitalsData): void => {
    if (isEditMode) {
      const modules: IEpxVitalsSchedule['modules'] = {};
      for (const key of Object.keys(rawSchedule.modules)) {
        modules[key] = {
          ...rawSchedule.modules[key],
          isActive: data.modules[key] ? data.modules[key].isActive : false
        };
      }
      _onSubmit({
        ...data,
        modules
      });
    } else {
      _onSubmit(data);
    }
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <ScheduleRepeat label="Repeat Messages" />
              </CardBlock>
              {props.values.mode === 'W' ? (
                <CardBreakout>
                  <Weekday name="days" inline />
                </CardBreakout>
              ) : null}
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <CheckboxGroup
                      label="Modules/Vitals to Collect"
                      name="modules"
                      options={[
                        {
                          label: 'Temperature',
                          value: 'temperature'
                        },
                        {
                          label: 'Blood Pressure',
                          value: 'bloodPressure'
                        },
                        {
                          label: 'Heart Rate',
                          value: 'heartRate'
                        },
                        {
                          label: 'Oxygen Levels (SpO2)',
                          value: 'oxygen'
                        },
                        {
                          label: 'Weight (Scale Required)',
                          value: 'weight'
                        },
                        {
                          label: 'Request Outreach',
                          value: 'requestCall'
                        }
                      ]}
                      minItems={1}
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxVitalsForm;
